import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, Layers, Plus, Trash2, ArrowUp, ArrowDown, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

// Curated list of preloaded Google Fonts. To add a new option: also add it
// to the <link> tag in /app/frontend/public/index.html so the font actually
// downloads on page load.
const FONT_OPTIONS = [
  { value: "'Cormorant Garamond', serif", label: 'Cormorant Garamond (elegant serif italic)' },
  { value: "'Syne', 'Space Grotesk', sans-serif", label: 'Syne (bold uppercase)' },
  { value: "'Fraunces', serif", label: 'Fraunces (craft italic serif)' },
  { value: "'Outfit', sans-serif", label: 'Outfit (bold geometric)' },
  { value: "'Bodoni Moda', serif", label: 'Bodoni Moda (luxury display)' },
  { value: "'Playfair Display', serif", label: 'Playfair Display (editorial default)' },
  { value: "'Great Vibes', cursive", label: 'Great Vibes (script)' },
];

const HERO_STYLES = [
  { value: 'editorial', label: 'Editorial — unchanged Mementa layout (Curated default)' },
  { value: 'cinematic', label: 'Cinematic — radial glow, expansive travel feel' },
  { value: 'craft', label: 'Craft — sideways warm gradient, studio-natural' },
  { value: 'workshop', label: 'Workshop — angled diagonal gradient, workbench' },
  { value: 'museum', label: 'Museum — centered edge glow, heirloom framed' },
];

const emptyForm = {
  slug: '',
  title: '',
  subtitle: '',
  tagline: '',
  font_family: FONT_OPTIONS[5].value,
  font_weight: 500,
  font_style: 'normal',
  text_transform: 'none',
  letter_spacing: '0',
  accent_color: '#FED7A5',
  accent_color_dark: '#FED7A5',
  text_accent: '#9E6752',
  hero_style: 'cinematic',
  enabled: true,
  order: 99,
};

export default function AdminArchetypes() {
  const [archetypes, setArchetypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null, title: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/archetypes');
      setArchetypes(res.data);
    } catch (e) {
      toast.error('Failed to load archetypes');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, order: archetypes.length });
    setShowDialog(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      slug: a.slug,
      title: a.title,
      subtitle: a.subtitle || '',
      tagline: a.tagline || '',
      font_family: a.font_family,
      font_weight: a.font_weight,
      font_style: a.font_style,
      text_transform: a.text_transform,
      letter_spacing: a.letter_spacing,
      accent_color: a.accent_color,
      accent_color_dark: a.accent_color_dark,
      text_accent: a.text_accent,
      hero_style: a.hero_style,
      enabled: a.enabled,
      order: a.order,
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (!editing && !form.slug.trim()) return toast.error('Slug is required');
    if (!editing && !/^[a-z0-9_]+$/.test(form.slug)) {
      return toast.error('Slug must be lowercase letters, numbers, and underscores only');
    }
    try {
      if (editing) {
        const payload = { ...form };
        delete payload.slug; // slug is immutable
        await api.put(`/admin/archetypes/${editing.id}`, payload);
        toast.success(`Updated ${form.title}`);
      } else {
        await api.post('/admin/archetypes', form);
        toast.success(`Created ${form.title}`);
      }
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const toggle = async (a) => {
    if (a.is_system && a.enabled) {
      return toast.info('System archetypes cannot be disabled');
    }
    try {
      await api.put(`/admin/archetypes/${a.id}`, { enabled: !a.enabled });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    }
  };

  const move = async (a, delta) => {
    const idx = archetypes.findIndex((x) => x.id === a.id);
    const swapWith = archetypes[idx + delta];
    if (!swapWith) return;
    try {
      await Promise.all([
        api.put(`/admin/archetypes/${a.id}`, { order: swapWith.order }),
        api.put(`/admin/archetypes/${swapWith.id}`, { order: a.order }),
      ]);
      load();
    } catch (e) {
      toast.error('Reorder failed');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/archetypes/${deleteModal.id}`);
      toast.success('Archetype deleted');
      setDeleteModal({ isOpen: false, id: null, title: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-archetypes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Layers className="w-7 h-7" style={{ color: '#9E6752' }} />
            Archetype Worlds
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage the gift archetype tiles that appear in the Browse carousel.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="flex items-center gap-2"
          style={{ background: '#9E6752', color: 'white' }}
          data-testid="admin-archetype-add-btn"
        >
          <Plus className="w-4 h-4" /> Add Archetype
        </Button>
      </div>

      <div className="rounded-xl border overflow-hidden bg-white dark:bg-neutral-900">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-neutral-800 text-left">
            <tr>
              <th className="p-3">Order</th>
              <th className="p-3">Title</th>
              <th className="p-3">Slug</th>
              <th className="p-3">Accent</th>
              <th className="p-3">Enabled</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {archetypes.map((a, i) => (
              <tr key={a.id} className="border-t" data-testid={`admin-archetype-row-${a.slug}`}>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <span className="w-6">{a.order}</span>
                    <button
                      onClick={() => move(a, -1)}
                      disabled={i === 0}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded disabled:opacity-30"
                      data-testid={`admin-archetype-up-${a.slug}`}
                    >
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => move(a, 1)}
                      disabled={i === archetypes.length - 1}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded disabled:opacity-30"
                      data-testid={`admin-archetype-down-${a.slug}`}
                    >
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium" style={{ fontFamily: a.font_family, fontWeight: a.font_weight, fontStyle: a.font_style, textTransform: a.text_transform, letterSpacing: a.letter_spacing }}>
                      {a.title}
                    </span>
                    {a.is_system && <Lock className="w-3 h-3 text-gray-400" title="System — cannot delete" />}
                  </div>
                  {a.subtitle && <div className="text-xs text-gray-500 mt-0.5">{a.subtitle}</div>}
                </td>
                <td className="p-3 font-mono text-xs text-gray-500">{a.slug}</td>
                <td className="p-3">
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-5 h-5 rounded-full border" style={{ background: a.accent_color }} />
                    <span className="text-xs">{a.accent_color}</span>
                  </div>
                </td>
                <td className="p-3">
                  <button
                    onClick={() => toggle(a)}
                    disabled={a.is_system}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-neutral-800 disabled:opacity-30"
                    title={a.is_system ? 'System archetype must stay enabled' : (a.enabled ? 'Disable' : 'Enable')}
                    data-testid={`admin-archetype-toggle-${a.slug}`}
                  >
                    {a.enabled
                      ? <Eye className="w-4 h-4" style={{ color: '#059669' }} />
                      : <EyeOff className="w-4 h-4 text-gray-400" />}
                  </button>
                </td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => openEdit(a)}
                    className="px-3 py-1 text-xs rounded border hover:bg-gray-100 dark:hover:bg-neutral-800 mr-2"
                    data-testid={`admin-archetype-edit-${a.slug}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteModal({ isOpen: true, id: a.id, title: a.title })}
                    disabled={a.is_system}
                    className="px-3 py-1 text-xs rounded border text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`admin-archetype-delete-${a.slug}`}
                  >
                    <Trash2 className="w-3 h-3 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-archetype-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit: ${editing.title}` : 'New Archetype'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editing && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Slug (URL key, immutable)</label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="e.g. eco_friendly"
                  data-testid="admin-archetype-slug-input"
                />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Title</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-archetype-title-input" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Subtitle</label>
                <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tagline</label>
              <Textarea value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} rows={2} />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Font family</label>
              <select
                value={form.font_family}
                onChange={(e) => setForm({ ...form, font_family: e.target.value })}
                className="w-full px-3 py-2 rounded-md border bg-white dark:bg-neutral-900"
                data-testid="admin-archetype-font-select"
              >
                {FONT_OPTIONS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              <div className="mt-2 p-3 rounded bg-gray-50 dark:bg-neutral-800 text-center" style={{ fontFamily: form.font_family, fontWeight: form.font_weight, fontStyle: form.font_style, textTransform: form.text_transform, letterSpacing: form.letter_spacing, fontSize: 22 }}>
                {form.title || 'Preview text'}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500">Weight</label>
                <select value={form.font_weight} onChange={(e) => setForm({ ...form, font_weight: parseInt(e.target.value) })} className="w-full px-2 py-1.5 rounded border bg-white dark:bg-neutral-900">
                  {[400, 500, 600, 700, 800].map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Style</label>
                <select value={form.font_style} onChange={(e) => setForm({ ...form, font_style: e.target.value })} className="w-full px-2 py-1.5 rounded border bg-white dark:bg-neutral-900">
                  <option value="normal">normal</option>
                  <option value="italic">italic</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Transform</label>
                <select value={form.text_transform} onChange={(e) => setForm({ ...form, text_transform: e.target.value })} className="w-full px-2 py-1.5 rounded border bg-white dark:bg-neutral-900">
                  <option value="none">none</option>
                  <option value="uppercase">uppercase</option>
                  <option value="lowercase">lowercase</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Tracking</label>
                <Input value={form.letter_spacing} onChange={(e) => setForm({ ...form, letter_spacing: e.target.value })} placeholder="0.04em" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500">Accent (light)</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-9 w-12 rounded border" />
                  <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="flex-1" data-testid="admin-archetype-accent-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Accent (dark mode)</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.accent_color_dark} onChange={(e) => setForm({ ...form, accent_color_dark: e.target.value })} className="h-9 w-12 rounded border" />
                  <Input value={form.accent_color_dark} onChange={(e) => setForm({ ...form, accent_color_dark: e.target.value })} className="flex-1" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Text accent (readable on white)</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.text_accent} onChange={(e) => setForm({ ...form, text_accent: e.target.value })} className="h-9 w-12 rounded border" />
                  <Input value={form.text_accent} onChange={(e) => setForm({ ...form, text_accent: e.target.value })} className="flex-1" />
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500">Hero style (world backdrop treatment)</label>
              <select value={form.hero_style} onChange={(e) => setForm({ ...form, hero_style: e.target.value })} className="w-full px-3 py-2 rounded border bg-white dark:bg-neutral-900">
                {HERO_STYLES.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                disabled={editing?.is_system}
              />
              Enabled {editing?.is_system && <span className="text-xs text-gray-500">(system archetype must stay enabled)</span>}
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} style={{ background: '#9E6752', color: 'white' }} data-testid="admin-archetype-save-btn">
              {editing ? 'Save changes' : 'Create archetype'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: null, title: '' })}
        onConfirm={confirmDelete}
        itemName={deleteModal.title}
        itemType="archetype"
      />
    </div>
  );
}
